import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'
import {
  ASSETS_TYPES,
  IEcommerceAnswerSchema,
  IEcommerceVideoAsset
} from '@aytm/app/swidget/base/questions/Ecommerce/types.ts'
import style from './Gallery.module.scss'
import React, { useState, useEffect, useMemo } from 'react'
import PopupImage from '@aytm/app/swidget/base/components/PopupImage.tsx'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { defaultAssetIndex } from '../../utils/defaultAssetIndex.ts'
import { getAssetType } from '../../utils/getAssetType.ts'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types.ts'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline.ts'
import { IFileAsset } from '../../../ShelfTest/types.ts'
import { getBigAssetPath } from '@aytm/lib/common/assets.ts'
import useAssetsCollectionBuilder from '../../hooks/useAssetsCollectionBuilder.ts'
import AssetsGate from '@aytm/app/swidget/base/questions/Ecommerce/components/AssetsGate.tsx'
import Spinner from './Spinner.tsx'

interface IGalleryProps {
  schema: IEcommerceAnswerSchema
  externalStyle?: string
  emulatedMobile: boolean
  iconsPosition?: string
  magnifierClass?: string
  onOpenAssetsModal?: (assetActiveIndex: number, tab: number) => void
}

const Gallery = ({ schema, externalStyle, emulatedMobile, magnifierClass, onOpenAssetsModal }: IGalleryProps) => {
  const { t } = useSurveyLocale()
  const noAltFallback = t('questions', 'img', 'noAlt')
  const timeline = useTimeline()
  const assetsCollection = useAssetsCollectionBuilder(schema)

  const [selectedAssetIndex, setSelectedAssetIndex] = useState<number>(() => defaultAssetIndex(assetsCollection))

  const videos = useMemo(
    () =>
      [...(schema.gallery || []), ...(schema.view3d ? [schema.view3d] : [])].filter(
        asset => ASSETS_TYPES.VIDEO === getAssetType(asset)
      ) as unknown as IEcommerceVideoAsset[],
    [schema]
  )

  const images = useMemo(
    () =>
      [
        ...(schema.image ? [schema.image] : []),
        ...(schema.gallery || []),
        ...(schema.view3d ? [schema.view3d] : [])
      ].filter(asset => ASSETS_TYPES.VIDEO !== getAssetType(asset)),
    [schema]
  )

  const CURRENT_ASSET_TYPE = getAssetType(assetsCollection[selectedAssetIndex])

  const onAssetSelect = (index: number) => {
    setSelectedAssetIndex(index)
  }

  useEffect(() => {
    timeline.log({
      ref: schema.id,
      type: 'show_asset',
      target: selectedAssetIndex.toString(),
      context: CURRENT_ASSET_TYPE.toLowerCase()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetIndex])

  return (
    <div className={`${style.gallery} ${externalStyle ? style[externalStyle] : ''}`}>
      <div className={style.assetsColumn}>
        {assetsCollection.map((asset, index) => (
          <div className={style.assetWrapper} key={index} onClick={() => onAssetSelect(index)}>
            {ASSETS_TYPES.IMAGE === getAssetType(asset) && (
              <AssetsGate
                urls={[(asset as IImageAsset).trimmed_path]}
                loader={<Spinner small className={`${style.collectionItem} ${style.imageItem}`} />}
              >
                <div
                  className={`
                    ${style.collectionItem}
                    ${style.imageItem}
                    ${index === selectedAssetIndex ? style.Active : ''}
                `}
                  role="img"
                  aria-label={(asset as IImageAsset).alt_text || noAltFallback}
                  style={{ backgroundImage: `url(${(asset as IImageAsset).trimmed_path})` }}
                />
              </AssetsGate>
            )}
            {ASSETS_TYPES.VIDEO === getAssetType(asset) && (
              <div
                className={`
                ${style.collectionItem}
                ${style.videoItem}
                ${index === selectedAssetIndex ? style.Active : ''}
              `}
              >
                <div className={style.layout} />
                <img
                  className={style.videoPreview}
                  src={(asset as IEcommerceVideoAsset).video.thumbnail}
                  alt=""
                  role="presentation"
                />
                <button className={style.play}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 5v14l11-7z" fill="#fff" />
                  </svg>
                </button>
              </div>
            )}
            {ASSETS_TYPES.MODEL === getAssetType(asset) && (
              <div
                className={`
                ${style.collectionItem}
                ${style.modelItem}
                ${index === selectedAssetIndex ? style.Active : ''}
            `}
              />
            )}
          </div>
        ))}
      </div>
      <div className={style.bigAsset}>
        {ASSETS_TYPES.IMAGE === CURRENT_ASSET_TYPE && (
          // A photo picked from the thumbs waits too: it is the one picture this box shows.
          <AssetsGate
            urls={[getBigAssetPath(assetsCollection[selectedAssetIndex] as IImageAsset, 'transparent')]}
            loader={<Spinner className={style.bigAssetLoader} />}
          >
            <div
              onClick={() => {
                onOpenAssetsModal(images.indexOf(assetsCollection[selectedAssetIndex] as IImageAsset), 1)
              }}
            >
              <PopupImage
                src={getBigAssetPath(assetsCollection[selectedAssetIndex] as IImageAsset, 'transparent')}
                altText={(assetsCollection[selectedAssetIndex] as IImageAsset).alt_text}
                enableMagnify={true}
                emulatedMobile={false}
                magnifierClass={magnifierClass}
                volume={false}
              />
            </div>
          </AssetsGate>
        )}
        {ASSETS_TYPES.MODEL === CURRENT_ASSET_TYPE && (
          <>
            {emulatedMobile ? (
              <div className={style.emulatedMobile}>
                <i className="icon-aytmfd-qshelf-ar"></i>
                <span>{t('questions', 'ec', 'amazon', 'preview3d')}</span>
              </div>
            ) : (
              <model-viewer
                src={(assetsCollection[selectedAssetIndex] as IFileAsset).file_url}
                ar
                camera-controls
                touch-action="pan-y"
              />
            )}
          </>
        )}
        {ASSETS_TYPES.VIDEO === CURRENT_ASSET_TYPE && (
          <div
            className={style.video}
            onClick={event => {
              event.stopPropagation()
              onOpenAssetsModal(videos.indexOf(assetsCollection[selectedAssetIndex] as IEcommerceVideoAsset), 0)
            }}
          >
            <img
              src={(assetsCollection[selectedAssetIndex] as IEcommerceVideoAsset).video.thumbnail}
              alt=""
              role="presentation"
            />
            <button className={style.play}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 5v14l11-7z" fill="#fff" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default Gallery
